import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { notification } from 'antd'
import { DeleteOutlined, FileImageOutlined, LoadingOutlined } from '@ant-design/icons'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { EventServices } from '@/middlewares/services'
import { useAuth } from '@/middlewares/auth/auth-context'
import { canDeletePartner } from '@/components/EventPage/Partners/index.utils'
import { ConvertPresentation, handleDeletePartner } from '@/components/EventPage/index.utils'
import { useEventHub } from './EventHubContext'
import EventHubConfirmDialog from './EventHubConfirmDialog'
import EventHubUploadModal from './EventHubUploadModal'
import { EVENT_HUB_UPLOAD_KIND } from './eventHub.upload.utils'
import {
    EVENT_HUB_DELETE_CONTROL_CLASS,
    EVENT_HUB_EDIT_CONTROL_SM_CLASS,
    EVENT_HUB_PARTNERS_MARGIN_BOTTOM_CLASS,
} from './eventHub.utils'

const SPEED = 40 // px/s
const HOVER_SPEED = 10 // px/s — slows on mouse hover, never fully stops

/**
 * Organizer add tile (Figma 3790:24903, mock `.org-add`): 40px dashed tile, 18px
 * image icon, 14px label with the 10px size hint, all #9498a6; hover turns border
 * and text blue. While a logo uploads the border stays blue and the text gives way
 * to "Uploading…" over an indeterminate blue bar (mock `.org-add.uploading`).
 * Opens the shared upload dialog (partner rules: 600 × 200 px max, 2 MB).
 */
function AddPartnerTile({ onOpen, isUploading }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)

    return (
        <button
            type="button"
            disabled={isUploading}
            onClick={onOpen}
            className={`s-focus-ring flex min-h-10 shrink-0 cursor-pointer items-center gap-2.5 rounded-[5px] border border-dashed bg-transparent px-3 py-1 text-left text-[#9498a6] transition-colors ${
                isUploading ? 'border-[#1672ec]' : 'border-[#dbdadf] hover:border-[#1672ec] hover:text-[#1672ec]'
            }`}
        >
            {isUploading ? <LoadingOutlined className="text-[18px]" /> : <FileImageOutlined className="text-[18px]" />}
            {isUploading ? (
                <span className="flex min-w-[110px] flex-col gap-1.5">
                    <span className="text-sm leading-[1.15]">{t('Uploading...')}</span>
                    <span className="h-1 overflow-hidden rounded-sm bg-[#dbdadf]">
                        <span className="block h-full w-3/5 rounded-sm bg-[#1672ec]" />
                    </span>
                </span>
            ) : (
                <span className="flex flex-col">
                    <span className="text-sm leading-[1.15]">{t('Add partner logo')}</span>
                    <span className="text-[10px] leading-[1.15]">{t('600 × 200 px max.')}</span>
                </span>
            )}
        </button>
    )
}

/**
 * Organizer variant (mock `body.org .ev-partners-track`) — the marquee stops and
 * the logos wrap into a fixed grid (24px rows, 48px columns) so a trash can never
 * moves under the pointer. Hovering a logo frames it in dashed blue with a 23px red
 * trash on its corner; an "Add partner logo" tile closes the row.
 */
function EditablePartners({ partners }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const authContext = useAuth()
    const { competition, setPresentation, setIsLoading } = useEventHub()
    const [isUploading, setIsUploading] = useState(false)
    const [isUploadOpen, setIsUploadOpen] = useState(false)
    const [partnerToDelete, setPartnerToDelete] = useState()

    const handleAdd = async (file) => {
        setIsUploading(true)
        try {
            const data = new FormData()
            data.append('file', file)
            const result = await EventServices.PartnerAdd(competition._id, data)
            if (result.data.presentation) {
                setPresentation(ConvertPresentation(result.data.presentation, competition))
                notification.success({ message: t('Partner added successfully') })
            }
        } catch (error) {
            console.error('/EditablePartners handleAdd - error', error)
            notification.error({ message: t('Something went worng, Please try again later or contact us') })
        }
        setIsUploading(false)
    }

    return (
        <div
            className={`flex flex-wrap items-center gap-x-12 gap-y-6 ${EVENT_HUB_PARTNERS_MARGIN_BOTTOM_CLASS}`}
            data-tour="hub-partners"
        >
            {(partners || []).map((partner) => (
                <div
                    key={partner._id}
                    className="group relative shrink-0 rounded-[5px] border border-dashed border-transparent p-1.5 transition-colors hover:border-[#1672ec] focus-within:border-[#1672ec]"
                >
                    <img
                        src={partner.logo}
                        alt=""
                        loading="lazy"
                        draggable={false}
                        className="h-[46px] w-auto max-w-[200px] object-contain"
                    />
                    {canDeletePartner(partner, authContext.user) && (
                        <button
                            type="button"
                            aria-label={t('Delete partner')}
                            onClick={() => setPartnerToDelete(partner)}
                            className={`${EVENT_HUB_EDIT_CONTROL_SM_CLASS} ${EVENT_HUB_DELETE_CONTROL_CLASS} absolute -right-3 -top-3 hidden group-hover:flex group-focus-within:flex`}
                        >
                            <DeleteOutlined />
                        </button>
                    )}
                </div>
            ))}
            <AddPartnerTile onOpen={() => setIsUploadOpen(true)} isUploading={isUploading} />

            {isUploadOpen && (
                <EventHubUploadModal
                    kind={EVENT_HUB_UPLOAD_KIND.PARTNER}
                    onClose={() => setIsUploadOpen(false)}
                    // Not awaited: the dialog closes and the tile shows "Uploading..." meanwhile.
                    onUpload={(files) => {
                        handleAdd(files[0])
                    }}
                />
            )}

            {partnerToDelete && (
                <EventHubConfirmDialog
                    title={t('Remove this partner logo?')}
                    confirmLabel={t('Remove logo')}
                    onConfirm={() => {
                        const partnerId = partnerToDelete._id
                        setPartnerToDelete()
                        handleDeletePartner(t, competition._id, partnerId, setIsLoading, setPresentation)
                    }}
                    onCancel={() => setPartnerToDelete()}
                />
            )}
        </div>
    )
}

/**
 * Partner logos. Public: auto-scrolling marquee (logos duplicated for a seamless
 * loop) with white edge fades; slows on mouse hover, touch devices keep full
 * speed; falls back to a static, manually scrollable row when content fits or
 * the user prefers reduced motion. Organizer (canEdit): editable static row.
 */
export default function EventHubPartners({ partners }) {
    const { canEdit } = useEventHub()
    const containerRef = useRef(null)
    const trackRef = useRef(null)
    const [marquee, setMarquee] = useState(false)

    useEffect(() => {
        if (canEdit) return undefined
        const container = containerRef.current
        const track = trackRef.current
        if (!container || !track) return undefined

        const firstSet = track.firstElementChild
        const update = () => {
            const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            setMarquee(!reduceMotion && firstSet.scrollWidth > container.clientWidth)
        }
        update()

        const resizeObserver = new ResizeObserver(update)
        resizeObserver.observe(container)
        resizeObserver.observe(firstSet)
        return () => resizeObserver.disconnect()
    }, [partners, canEdit])

    useEffect(() => {
        if (!marquee || canEdit) return undefined
        const container = containerRef.current
        const track = trackRef.current

        let speed = SPEED
        let offset = 0
        let last = performance.now()
        let raf = requestAnimationFrame(function tick(now) {
            const dt = Math.min(now - last, 100) / 1000
            last = now
            // one logo set + one gap = distance after which the loop repeats
            const period = track.scrollWidth - track.firstElementChild.offsetWidth
            offset = (offset + speed * dt) % period
            track.style.transform = `translate3d(${-offset}px,0,0)`
            raf = requestAnimationFrame(tick)
        })

        const slowDown = (e) => {
            // touch "hover" (tap) must not slow the marquee on mobile
            if (e.pointerType === 'mouse') speed = HOVER_SPEED
        }
        const restore = () => {
            speed = SPEED
        }
        container.addEventListener('pointerenter', slowDown)
        container.addEventListener('pointerleave', restore)

        return () => {
            cancelAnimationFrame(raf)
            container.removeEventListener('pointerenter', slowDown)
            container.removeEventListener('pointerleave', restore)
            track.style.transform = ''
        }
    }, [marquee, canEdit])

    if (canEdit) {
        return <EditablePartners partners={partners} />
    }

    if (!partners?.length) {
        return null
    }

    const logoSet = (hidden) => (
        <ul aria-hidden={hidden || undefined} className="m-0 flex h-12 w-max list-none items-center gap-[70px] p-0">
            {partners.map((partner) => (
                <li key={partner._id} className="flex shrink-0 items-center">
                    <img
                        src={partner.logo}
                        alt=""
                        loading="lazy"
                        draggable={false}
                        className="h-[46px] w-auto max-w-[200px] object-contain"
                    />
                </li>
            ))}
        </ul>
    )

    return (
        <div
            ref={containerRef}
            aria-label="Partners"
            className={[
                `relative w-full overflow-hidden ${EVENT_HUB_PARTNERS_MARGIN_BOTTOM_CLASS}`,
                marquee
                    ? // mock `.ev-partners` mask: 50px fades on both edges
                      "before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:z-10 before:w-[50px] before:bg-linear-to-r before:from-white before:to-white/0 before:content-[''] after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:z-10 after:w-[50px] after:bg-linear-to-l after:from-white after:to-white/0 after:content-['']"
                    : '',
            ].join(' ')}
        >
            <div
                ref={trackRef}
                className={
                    marquee
                        ? 'flex w-max items-center gap-[70px]'
                        : 'overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]'
                }
            >
                {logoSet(false)}
                {marquee && logoSet(true)}
            </div>
        </div>
    )
}
